import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, Pencil, Trash2 } from "lucide-react";

/**
 * Owner-only audit alerts feed. Every Delete or Edit on a committed shift
 * item (Invoice or Cashflow row) is logged server-side and surfaced here so
 * the Owner can spot who is reversing or editing transactions.
 */
export default function OwnerAuditAlerts() {
  const [alerts, setAlerts] = useState([]);
  // Iter 116 · Client-side "dismissed" set persisted in localStorage so the
  // 30s auto-refresh doesn't resurrect rows the owner cleared. No backend
  // endpoint required — this is a visual clear-from-view only.
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem("kf_audit_dismissed");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });

  const persistDismissed = (next) => {
    try {
      localStorage.setItem("kf_audit_dismissed", JSON.stringify([...next]));
    } catch { /* quota etc. — ignore */ }
  };

  const dismissRow = (id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      persistDismissed(next);
      return next;
    });
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  };

  const load = async () => {
    try {
      const { data } = await api.get("/portal/audit-alerts");
      setAlerts((data || []).filter((a) => !dismissed.has(a.id)));
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(i);
  }, []);

  return (
    <Card className="p-5 mb-6 border-l-4 border-l-red-600" data-testid="owner-audit-alerts">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-red-50 text-red-700 flex items-center justify-center">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold text-lg leading-tight">Owner Audit Alerts</h3>
            <p className="text-[11px] text-slate-500 tracking-wide">
              Every reversal or edit on a committed transaction — who, what, how much
            </p>
          </div>
        </div>
        <Badge className="bg-slate-900 text-white font-mono" data-testid="audit-count">
          {alerts.length}
        </Badge>
      </div>

      {alerts.length === 0 ? (
        <div className="text-sm text-slate-400 text-center py-4 border-2 border-dashed border-slate-200 rounded-lg" data-testid="audit-empty">
          No reversals or edits yet — every committed transaction is intact.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
          {alerts.map((a) => {
            const Icon = a.action === "delete" ? Trash2 : Pencil;
            const color = a.action === "delete" ? "text-red-700" : "text-amber-700";
            const bg = a.action === "delete" ? "bg-red-50 border-red-100" : "bg-amber-50 border-amber-100";
            return (
              <div
                key={a.id}
                data-testid={`audit-row-${a.id}`}
                className={`flex items-center gap-3 border rounded-lg px-3 py-2 ${bg}`}
              >
                <Icon className={`h-4 w-4 shrink-0 ${color}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-900 truncate">
                    <span className={`font-bold uppercase tracking-wide text-[10px] mr-2 ${color}`}>
                      {a.action}
                    </span>
                    <span className="text-slate-700">{a.employee_name}</span>
                    <span className="text-slate-400"> @ {a.location || "—"}</span>
                  </div>
                  <div className="text-[11px] font-mono text-slate-600 truncate">
                    {a.entity.toUpperCase()} · {a.detail || "—"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-bold text-sm text-slate-900">
                    {a.action === "delete"
                      ? `−€${Math.abs(Number(a.amount_before) || 0).toFixed(2)}`
                      : `€${Number(a.amount_before).toFixed(2)} → €${Number(a.amount_after).toFixed(2)}`}
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono">
                    {new Date(a.created_at).toLocaleString()}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => dismissRow(a.id)}
                  data-testid={`audit-delete-${a.id}`}
                  title="Καθαρισμός εγγραφής · Clear alert"
                  className="shrink-0 inline-flex items-center rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-[11px] font-bold text-rose-700 hover:bg-rose-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
